import React, { useState } from "react";


import FormSelect from "@/framework/forms/FormSelect";
import { useSEMasters } from "@/modules/common/hooks/useSEMasters";
import { InvoiceResponse } from "@/api";

export interface InvoiceDetailsEditProps {
  value: InvoiceResponse;

  onChange: (
    value: InvoiceResponse,
  ) => void;

}

export default function InvoiceDetailsEdit({
  value,
  onChange,
}: InvoiceDetailsEditProps) {
  const [error, setError] =
    useState<string | null>(null);


  return (
    <section className="rounded-xl border bg-white p-6">

      <InvoiceDetailsFields
        value={value}
        onChange={onChange}
      />

      {error && (
        <div className="mt-4 text-sm text-red-600">
          {error}
        </div>
      )}
    </section>
  );
}

/*
 * --------------------------------------------------
 * FIELDS
 * --------------------------------------------------
 */

function InvoiceDetailsFields({
  value,
  onChange,
}: {
  value: InvoiceResponse;
  onChange: (
    value: InvoiceResponse,
  ) => void;
}) {
  const {
    invoiceSalesBy,
    gstStates,
  } = useSEMasters();

  const salesByOptions =
    (invoiceSalesBy ?? []).map(
      (salesBy) => ({
        value: String(salesBy.id),
        label: salesBy.code,
      }),
    );

  const supplyStateOptions =
    (gstStates ?? []).map(
      (state) => ({
        value: String(state.id),
        label: state.code,
      }),
    );

  const update = <
    K extends keyof InvoiceResponse
  >(
    field: K,
    fieldValue: InvoiceResponse[K],
  ) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <FormSelect
        label="Sales By"
        value={
          value.sales_by
            ? String(value.sales_by)
            : ""
        }
        options={salesByOptions}
        onChange={(salesBy) => {
          update(
            "sales_by",
            String(salesBy),
          );
        }}
      />

      <Input
        label="Supply Place"
        value={
          value.supply_place ?? ""
        }
        onChange={(v) =>
          update(
            "supply_place",
            v,
          )
        }
      />

      <FormSelect
        label="Supply State"
        value={
          value.supply_state
            ? String(value.supply_state_code)
            : ""
        }
        options={supplyStateOptions}
        onChange={(stateId) => {
          const selectedState =
            gstStates?.find(
              (state) =>
                String(state.id) === String(stateId),
            );

          if (!selectedState) {
            return;
          }

          onChange({
            ...value,
            supply_state: selectedState.code,
            supply_state_code: selectedState.id,
          });
        }}
      />

      <Input
        label="Requestor Vendor Code"
        value={
          value.requestor_vendor_code ??
          ""
        }
        onChange={(v) =>
          update(
            "requestor_vendor_code",
            v,
          )
        }
      />

      <Input
        label="Requestor Vendor SPOC"
        value={
          value.requestor_vendor_spoc ??
          ""
        }
        onChange={(v) =>
          update(
            "requestor_vendor_spoc",
            v,
          )
        }
      />

      <Input
        label="GST Number"
        value={
          value.gst_number ?? ""
        }
        onChange={(v) =>
          update(
            "gst_number",
            v,
          )
        }
      />

      <Input
        label="PAN Number"
        value={
          value.pan_number ?? ""
        }
        onChange={(v) =>
          update(
            "pan_number",
            v,
          )
        }
      />
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label}
      </label>

      <input
        type="text"
        value={value}
        onChange={(event) =>
          onChange(
            event.target.value,
          )
        }
        className="w-full rounded-md border px-3 py-2"
      />
    </div>
  );
}